// FILE: src/components/case-studies/exam/CaseReviewGroup.js
//
// Results-page grouping for case blocks (Phase 2, PR 6). Pure helper +
// header component; ReviewItem itself is untouched.

import React, { useState } from 'react';
import CasePanel from '../learner/CasePanel';

const T = {
  line: 'var(--line, #e5e8f1)', muted: 'var(--muted, #5a6582)',
  brand: 'var(--brand, #2c63ff)', brandSoft: 'var(--brand-soft, #eaf0ff)',
  good: 'var(--good, #16a571)', panel: 'var(--panel, #ffffff)',
};

/**
 * Annotate a (possibly filtered) review-item list with case group starts.
 * Returns [{ item, caseHeader: {caseId} | null }] — a header is emitted
 * whenever an item opens a case group the previous item didn't belong to,
 * so it works under any filter.
 */
export function groupReviewItems(items) {
  let prevCaseId = null;
  return items.map((item) => {
    const caseId = item.question && item.question.caseId;
    const header = caseId && caseId !== prevCaseId ? { caseId } : null;
    prevCaseId = caseId || null;
    return { item, caseHeader: header };
  });
}

/** Per-case correct/total across ALL items (unfiltered), for the header. */
export function caseScoreStats(allItems) {
  const stats = {};
  allItems.forEach((item) => {
    const caseId = item.question && item.question.caseId;
    if (!caseId) return;
    const s = stats[caseId] || { correct: 0, total: 0 };
    s.total += 1;
    if (item.status === 'correct') s.correct += 1;
    stats[caseId] = s;
  });
  return stats;
}

export function CaseReviewHeader({ block, stats }) {
  const [open, setOpen] = useState(false);
  if (!block) return null;
  const s = stats || { correct: 0, total: block.questionCount };
  return (
    <div data-testid={`case-review-header-${block.slug || block.id}`}
      style={{ border: `1px solid ${T.brand}`, background: T.brandSoft, borderRadius: 10,
        padding: '10px 14px', margin: '14px 0 8px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', color: T.brand }}>🗂️ Case Study</span>
        <strong style={{ fontSize: '0.92rem' }}>{block.title}</strong>
        <span style={{ fontSize: '0.78rem', color: T.muted }}>
          Questions {block.startIndex + 1}–{block.endIndex + 1}
        </span>
        <span style={{ padding: '1px 10px', borderRadius: 999, fontSize: '0.74rem', fontWeight: 700,
          background: T.panel, color: s.correct === s.total ? T.good : T.muted }}>
          {s.correct}/{s.total} correct
        </span>
        <span style={{ flex: 1 }} />
        <button type="button" data-testid="case-review-context-toggle"
          style={{ border: `1px solid ${T.line}`, background: T.panel, borderRadius: 6, cursor: 'pointer', padding: '3px 10px', fontSize: '0.76rem', fontWeight: 600 }}
          onClick={() => setOpen((o) => !o)}>
          {open ? 'Hide scenario ▲' : 'Review scenario ▼'}
        </button>
      </div>
      {open && (
        <div style={{ background: T.panel, borderRadius: 8, padding: 12, marginTop: 10, maxHeight: '46vh', overflowY: 'auto' }}>
          <CasePanel caseMeta={{ title: block.title, learnerRole: block.learnerRole }}
            stages={block.stages || []} latestStage={null} />
        </div>
      )}
    </div>
  );
}
